// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

/**
 * D-SUP-2 / D-SUPPORT-3: the agent queue.
 *  - Three lanes (Patrick 2026-08-17): QUEUE = open work only, MY QUEUE = my open
 *    work, ARCHIVE = solved/closed/resolved. Closed cases never pollute the
 *    working lanes.
 *  - Excel-grade grid: click-to-sort headers, hide/reorder via the shared
 *    ColumnManager, per-column resize via the shared ColumnResize hooks — the
 *    SAME primitives the Rx grids use (L-067: shared mounted unit, not a fork).
 *  - Lane, filters, sort and paging are SERVER-side (D-SUPPORT-3 review #7): the tab counts
 *    and the summary tiles are both computed over the full data set, so they cannot disagree,
 *    and nothing is hidden behind a client-side window. The sidebar badges come from
 *    GET /support/badges.
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { ErrorBanner, LoadingState, EmptyState } from '../../components/workflow/StatePills'
import { StatusBadge } from './supportBadges'
import { useServerPaged, Pager, DEFAULT_PAGE_SIZE } from '../../components/Pager'
import { useColumnManager } from '../../components/ColumnManager'
import { useColumnWidths, ColumnResizeHandle } from '../../components/common/ColumnResize'
import { useServerFunnels, type ServerFunnelColumn } from '../../components/common/TableFunnels'

const SEVERITIES: (api.SupportSeverity | '')[] = ['', 'critical', 'high', 'normal', 'low']
const OPEN_STATUS_OPTS = ['', 'new', 'open', 'triage', 'in_progress', 'pending', 'on_hold']
const ARCHIVE_STATUS_OPTS = ['', 'resolved', 'solved', 'closed']
const STATUS_FILTER_LABELS: Record<string, string> = {
  new: 'New', open: 'Open', triage: 'Triage', in_progress: 'In progress',
  pending: 'Pending', on_hold: 'On hold', resolved: 'Resolved', solved: 'Solved', closed: 'Closed',
}

type Lane = 'queue' | 'mine' | 'archive'
type Row = api.SupportQueueItem

// Sorting is server-side (the column key is sent as ?sort=), so a column is an id + a label.
interface ColDef { key: string; label: string }
const COLUMNS: ColDef[] = [
  { key: 'ticket_no', label: '#' },
  { key: 'pharmacy', label: 'Pharmacy' },
  { key: 'requester', label: 'Requester' },
  { key: 'subject', label: 'Subject' },
  { key: 'priority', label: 'Priority' },
  { key: 'status', label: 'Status' },
  { key: 'assigned', label: 'Assigned' },
  { key: 'age', label: 'Age' },
  { key: 'response', label: 'Response' },
  { key: 'resolution', label: 'Resolution' },
  // The clock that did not exist before migration 369: a case can be inside target on both the
  // others while a customer who wrote days ago is still waiting.
  { key: 'reply', label: 'Reply due' },
]
const COL_LABELS = Object.fromEntries(COLUMNS.map(c => [c.key, c.label]))

// D-UI-2: header funnels. The queue is server-paged, so a funnel is sent to the endpoint
// (`filter=`) and ANDs with the toolbar's status / severity / tag / search. Keys are the column
// keys. Response, Resolution and Reply due have none: they are SLA clock states worked out in C#
// against the business calendar after the query, so there is nothing in SQL to compare.
const FUNNEL_COLS: ServerFunnelColumn[] = [
  { key: 'ticket_no', label: '#', kind: 'text' },
  { key: 'pharmacy', label: 'Pharmacy', kind: 'text' },
  { key: 'requester', label: 'Requester', kind: 'text' },
  { key: 'subject', label: 'Subject', kind: 'text' },
  { key: 'priority', label: 'Priority', kind: 'list', options: ['P1', 'P2', 'P3', 'P4'] },
  { key: 'status', label: 'Status', kind: 'list', options: Object.entries(STATUS_FILTER_LABELS).map(([value, label]) => ({ value, label })) },
  { key: 'assigned', label: 'Assigned', kind: 'list', options: [{ value: 'Assigned', label: 'Assigned' }, { value: 'Unassigned', label: '— (unassigned)' }] },
  // The cell reads "3d 4h"; the funnel takes the same age as a number of days.
  { key: 'age', label: 'Age (days)', kind: 'number' },
]

export default function SupportQueuePage() {
  const navigate = useNavigate()
  const [sp, setSp] = useSearchParams()
  const lane = (['queue', 'mine', 'archive'].includes(sp.get('tab') ?? '') ? sp.get('tab') : 'queue') as Lane
  const setLane = (l: Lane) => { const n = new URLSearchParams(sp); l === 'queue' ? n.delete('tab') : n.set('tab', l); setSp(n, { replace: true }) }

  const [summary, setSummary] = useState<api.SupportQueueSummary | null>(null)
  const [laneCounts, setLaneCounts] = useState({ queue: 0, mine: 0, archive: 0 })
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<string>('')
  const [severity, setSeverity] = useState<api.SupportSeverity | ''>('')
  const [tag, setTag] = useState('')
  const [search, setSearch] = useState('')
  const [searchQ, setSearchQ] = useState('')  // debounced value that actually queries
  const [tags, setTags] = useState<{ tag: string; uses: number }[]>([])
  const [views, setViews] = useState<api.SupportView[]>([])
  const [activeView, setActiveView] = useState('')
  const [sortKey, setSortKey] = useState<string>('age')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  useEffect(() => {
    api.listSupportTags().then(r => setTags(r.items)).catch(() => {})
    api.listSupportViews().then(r => setViews(r.items)).catch(() => {})
  }, [])

  // W4: server-side search, debounced; 0 or 2+ chars (a single char matches half the queue).
  useEffect(() => {
    const t = setTimeout(() => setSearchQ(search.trim().length >= 2 ? search.trim() : ''), 350)
    return () => clearTimeout(t)
  }, [search])

  const fx = useServerFunnels(FUNNEL_COLS)

  // ONE request answers the whole screen: the page's rows, the three lane tab counts and the
  // dashboard summary — all evaluated server-side over every ticket, not over a capped window.
  const paged = useServerPaged<Row>(
    ({ page, pageSize }) =>
      api.getSupportQueue({
        lane, page, pageSize, sort: sortKey, dir: sortDir,
        ...(status ? { status } : {}),
        ...(severity ? { severity } : {}),
        ...(tag ? { tag } : {}),
        ...(searchQ ? { q: searchQ } : {}),
        filter: fx.filter,
      }).then(r => {
        setSummary(r.summary)
        setLaneCounts(r.lane_counts)
        return {
          items: r.items, total: r.total, page: r.page, page_size: r.page_size,
          total_pages: Math.max(1, Math.ceil(r.total / Math.max(1, r.page_size))),
        }
      }),
    DEFAULT_PAGE_SIZE,
    [lane, status, severity, tag, searchQ, sortKey, sortDir, fx.sig],
  )
  const rows = paged.rows
  const loading = paged.loading
  useEffect(() => { if (paged.error) setError(friendlyError(paged.error)) }, [paged.error])

  // Excel-grade columns: hide/reorder (persisted) + widths (persisted per lane).
  const { visibleColumns: visKeys, managerUI, toggleButton } = useColumnManager('support_queue', COLUMNS.map(c => c.key), COL_LABELS)
  const visibleCols = useMemo(() => visKeys.map(k => COLUMNS.find(c => c.key === k)).filter((c): c is ColDef => !!c), [visKeys])
  const colResize = useColumnWidths(`support_queue:${lane}`, visibleCols.map(c => c.key))

  const clickSort = (k: string) => {
    if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(k); setSortDir(k === 'age' ? 'desc' : 'asc') }
  }

  const applyView = (viewId: string) => {
    setActiveView(viewId)
    if (!viewId) { setStatus(''); setSeverity(''); setTag(''); return }
    const v = views.find(x => x.id === viewId)
    if (!v) return
    try {
      const f = JSON.parse(v.filter) as { status?: string; severity?: api.SupportSeverity; tag?: string }
      setStatus(f.status ?? ''); setSeverity(f.severity ?? ''); setTag(f.tag ?? '')
    } catch { /* malformed saved filter */ }
  }

  const saveCurrentView = async () => {
    const name = window.prompt('Name this view:')
    if (!name?.trim()) return
    try {
      const filter = { ...(status ? { status } : {}), ...(severity ? { severity } : {}), ...(tag ? { tag } : {}) }
      const r = await api.createSupportView(name.trim(), filter)
      const vs = await api.listSupportViews()
      setViews(vs.items); setActiveView(r.id)
    } catch (e) { setError(friendlyError(e)) }
  }

  const statusOpts = lane === 'archive' ? ARCHIVE_STATUS_OPTS : OPEN_STATUS_OPTS

  return (
    <div style={{ padding: '24px 24px 48px', maxWidth: 'var(--page-max, 1280px)', margin: '0 auto', width: '100%' }}>
      <header style={{ marginBottom: 14 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Support queue</h1>
        <div style={{ color: 'var(--color-text-mute)', fontSize: 13, marginTop: 4 }}>Tickets across all pharmacies.</div>
      </header>

      {/* Lanes: Queue / My queue / Archive */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }} role="tablist" aria-label="Queue lanes">
        <LaneTab label="Queue" count={laneCounts.queue} active={lane === 'queue'} onClick={() => { setLane('queue'); setStatus('') }} />
        <LaneTab label="My queue" count={laneCounts.mine} active={lane === 'mine'} onClick={() => { setLane('mine'); setStatus('') }} />
        <LaneTab label="Archive" count={laneCounts.archive} active={lane === 'archive'} onClick={() => { setLane('archive'); setStatus('') }} muted />
      </div>

      {summary && lane !== 'archive' && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          <Stat label="Open" value={summary.open} />
          <Stat label="Unassigned" value={summary.unassigned} />
          <Stat label="SLA breached" value={summary.breached} tone={summary.breached > 0 ? 'bad' : 'ok'} />
          <Stat label="Due soon" value={summary.due_soon} tone={summary.due_soon > 0 ? 'warn' : 'ok'} />
          <Stat label="Paused" value={summary.paused ?? 0} />
          <Stat label="Oldest open" value={fmtAge(summary.oldest_age_seconds)} />
          <Stat label="P1 / P2 / P3 / P4" value={`${summary.by_priority.P1} / ${summary.by_priority.P2} / ${summary.by_priority.P3} / ${summary.by_priority.P4}`} />
        </div>
      )}

      {summary?.by_pharmacy && summary.by_pharmacy.length > 0 && lane === 'queue' && (
        <div style={{ background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border)', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: 'var(--color-text-mute)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>Open tickets by pharmacy</div>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
            {summary.by_pharmacy.map(p => (
              <div key={p.pharmacy_id} style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span style={{ fontSize: 13.5, fontWeight: 600 }}>{p.pharmacy_name ?? p.pharmacy_id.slice(0, 8)}</span>
                <span style={{ fontSize: 13, color: 'var(--color-text-mute)' }}>{p.open}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
        <input type="search" value={search} onChange={e => setSearch(e.target.value)}
               placeholder="Search # / subject / pharmacy / reporter…"
               style={{ ...filterSel, textTransform: 'none', minWidth: 240 }} />
        {views.length > 0 && (
          <select value={activeView} onChange={e => applyView(e.target.value)} style={{ ...filterSel, fontWeight: 600 }} title="Saved views">
            <option value="">Custom filters</option>
            {views.map(v => <option key={v.id} value={v.id}>{v.shared ? '👥 ' : ''}{v.name}</option>)}
          </select>
        )}
        <select value={status} onChange={e => { setStatus(e.target.value); setActiveView('') }} style={filterSel}>
          {statusOpts.map(s => <option key={s} value={s}>{s === '' ? 'All statuses' : (STATUS_FILTER_LABELS[s] ?? s)}</option>)}
        </select>
        <select value={severity} onChange={e => { setSeverity(e.target.value as api.SupportSeverity | ''); setActiveView('') }} style={filterSel}>
          {SEVERITIES.map(s => <option key={s} value={s}>{s === '' ? 'All severities' : s}</option>)}
        </select>
        {tags.length > 0 && (
          <select value={tag} onChange={e => { setTag(e.target.value); setActiveView('') }} style={filterSel}>
            <option value="">All tags</option>
            {tags.map(t => <option key={t.tag} value={t.tag}>{t.tag} ({t.uses})</option>)}
          </select>
        )}
        {(status || severity || tag) && (
          <button onClick={saveCurrentView} style={{ ...filterSel, cursor: 'pointer', textTransform: 'none' }} title="Save the current filters as a view">
            + Save view
          </button>
        )}
        <span style={{ flex: 1 }} />
        {toggleButton}
      </div>
      {managerUI}

      {error && <ErrorBanner message={error} onClose={() => setError(null)} />}

      {fx.chips}
      <div style={cardStyle}>
        {/* While a funnel is set the table stays mounted, loading or empty: the header is where
            the funnel is cleared, and unmounting it would close the funnel being typed in. */}
        {loading && !fx.active ? <LoadingState label="Loading queue…" /> :
         rows.length === 0 && !fx.active ? <EmptyState message={lane === 'archive' ? 'No archived tickets match.' : 'No open tickets match these filters.'} /> :
        <><Pager {...paged.pagerProps} label="tickets" />
        <div style={{ overflowX: 'auto', opacity: loading ? 0.6 : 1 }}>
        <table ref={colResize.tableRef} {...fx.tableProps} style={{ ...tableStyle, tableLayout: colResize.tableLayout }}>
          <colgroup>
            {visibleCols.map(c => <col key={c.key} style={{ width: colResize.widthFor(c.key) }} />)}
          </colgroup>
          <thead>
            <tr>
              {visibleCols.map(c => (
                <th key={c.key} style={{ ...thStyle, cursor: 'pointer', userSelect: 'none', position: 'relative', whiteSpace: 'nowrap' }}
                    onClick={() => clickSort(c.key)}>
                  {c.label}{sortKey === c.key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                  {fx.funnel(c.key)}
                  <ColumnResizeHandle colKey={c.key} colIndex={visibleCols.indexOf(c)} api={colResize} label={c.label} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={Math.max(1, visibleCols.length)} style={{ ...tdStyle, color: 'var(--color-text-mute)' }}>
                {loading ? 'Loading…' : 'No tickets match these filters.'}
              </td></tr>
            )}
            {rows.map(t => (
              <tr key={t.id} style={{ borderTop: '1px solid var(--color-border)', cursor: 'pointer' }}
                  onClick={() => navigate(`/admin/support/${t.id}`)} tabIndex={0} role="button"
                  onKeyDown={e => { if (e.key === 'Enter') navigate(`/admin/support/${t.id}`) }}>
                {visibleCols.map(c => <Cell key={c.key} col={c.key} t={t} />)}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <Pager {...paged.pagerProps} showPageSize={false} label="tickets" /></>}
      </div>
    </div>
  )
}

function Cell({ col, t }: { col: string; t: Row }) {
  switch (col) {
    case 'ticket_no': return <td style={tdStyle}>{t.ticket_no}</td>
    case 'pharmacy': return <td style={tdEllipsis}>{t.pharmacy_name || '—'}</td>
    case 'requester': return <td style={tdEllipsis}>{t.requester_name || '—'}</td>
    case 'subject': return (
      <td style={{ ...tdEllipsis, fontWeight: 600 }} title={t.subject}>
        {t.subject}
        {(t.tags?.length ?? 0) > 0 && (
          <div style={{ marginTop: 3, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {t.tags!.map(tg => <span key={tg} style={tagChip}>{tg}</span>)}
          </div>
        )}
      </td>)
    case 'priority': return <td style={tdStyle}><PriorityBadge priority={t.priority} severity={t.severity} /></td>
    case 'status': return <td style={tdStyle}><StatusBadge status={t.status} /></td>
    case 'assigned': return <td style={tdStyle}>{t.assigned_to_account_id ? 'Assigned' : '—'}</td>
    case 'age': return <td style={{ ...tdStyle, color: t.sla_status === 'breached' ? '#b91c1c' : 'inherit', fontWeight: t.sla_status === 'breached' ? 700 : 400 }}>{fmtAge(t.age_seconds)}</td>
    case 'response': return <td style={tdStyle}><SlaBadge status={t.fr_status ?? t.sla_status} remaining={t.fr_remaining_minutes} /></td>
    case 'resolution': return <td style={tdStyle}><SlaBadge status={t.res_status ?? 'none'} remaining={t.res_remaining_minutes} /></td>
    case 'reply': return <td style={tdStyle}><SlaBadge status={t.nr_status ?? 'none'} remaining={t.nr_remaining_minutes} /></td>
    default: return <td style={tdStyle}>—</td>
  }
}

function LaneTab({ label, count, active, onClick, muted }: { label: string; count: number; active: boolean; onClick: () => void; muted?: boolean }) {
  return (
    <button role="tab" aria-selected={active} onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 14px', borderRadius: 9,
        border: '1px solid var(--color-border)', fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
        background: active ? 'var(--color-primary, #0f766e)' : 'var(--color-surface)',
        color: active ? '#fff' : muted ? 'var(--color-text-mute)' : 'var(--color-text)',
      }}>
      {label}
      <span style={{
        minWidth: 20, textAlign: 'center', padding: '1px 7px', borderRadius: 999, fontSize: 12, fontWeight: 700,
        background: active ? 'rgba(255,255,255,0.25)' : 'var(--color-bg)', color: active ? '#fff' : 'var(--color-text-mute)',
      }}>{count}</span>
    </button>
  )
}

function Stat({ label, value, tone }: { label: string; value: string | number; tone?: 'ok' | 'warn' | 'bad' }) {
  const color = tone === 'bad' ? '#b91c1c' : tone === 'warn' ? '#b45309' : 'var(--color-text)'
  return (
    <div style={{ flex: '1 1 130px', minWidth: 110, background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 10, padding: '10px 12px' }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.4px', color: 'var(--color-text-mute)', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color, marginTop: 2 }}>{value}</div>
    </div>
  )
}

const PRIO_COLOR: Record<string, { bg: string; fg: string }> = {
  P1: { bg: '#fee2e2', fg: '#b91c1c' }, P2: { bg: '#fef3c7', fg: '#b45309' },
  P3: { bg: '#e0f2fe', fg: '#0369a1' }, P4: { bg: '#eef2f1', fg: '#4b5563' },
}
function PriorityBadge({ priority, severity }: { priority: string; severity: string }) {
  const c = PRIO_COLOR[priority] ?? PRIO_COLOR.P4
  return <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 999, fontSize: 12, fontWeight: 700, background: c.bg, color: c.fg }} title={severity}>{priority}</span>
}

const SLA_LABEL: Record<string, { label: string; bg: string; fg: string }> = {
  breached: { label: 'Breached', bg: '#fee2e2', fg: '#b91c1c' },
  due_soon: { label: 'Due soon', bg: '#fef3c7', fg: '#b45309' },
  ok: { label: 'On track', bg: '#dcfce7', fg: '#15803d' },
  met: { label: 'Met', bg: '#dcfce7', fg: '#15803d' },
  missed: { label: 'Missed', bg: '#fee2e2', fg: '#b91c1c' },
  paused: { label: 'Paused', bg: '#e5e7eb', fg: '#374151' },
  none: { label: 'No SLA', bg: '#eef2f1', fg: '#64748b' },
}
function SlaBadge({ status, remaining }: { status: string; remaining?: number | null }) {
  const c = SLA_LABEL[status] ?? SLA_LABEL.none
  const hint = (status === 'ok' || status === 'due_soon') && typeof remaining === 'number' && remaining > 0
    ? ` · ${fmtMins(remaining)}` : ''
  return <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: c.bg, color: c.fg, whiteSpace: 'nowrap' }}>{c.label}{hint}</span>
}
function fmtMins(m: number): string {
  if (m < 60) return `${Math.round(m)}m`
  if (m < 60 * 24) return `${Math.round(m / 60)}h`
  return `${Math.round(m / 60 / 24)}d`
}
function fmtAge(seconds: number): string {
  if (seconds < 3600) return `${Math.max(1, Math.floor(seconds / 60))}m`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`
  return `${Math.floor(seconds / 86400)}d ${Math.floor((seconds % 86400) / 3600)}h`
}

const cardStyle: React.CSSProperties = { background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 8 }
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13.5, minWidth: 980 }
const tagChip: React.CSSProperties = { fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 999, background: '#eef2ff', color: '#4338ca' }
const thStyle: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', color: 'var(--color-text-mute)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.4px' }
const tdStyle: React.CSSProperties = { padding: '10px 10px' }
const tdEllipsis: React.CSSProperties = { padding: '10px 10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
const filterSel: React.CSSProperties = { padding: '7px 10px', borderRadius: 8, border: '1px solid var(--color-border)', fontSize: 13.5, background: 'var(--color-bg)', color: 'var(--color-text)', textTransform: 'capitalize' }
